import groupBy from 'lodash/groupBy'
import orderBy from 'lodash/orderBy'
import partition from 'lodash/partition'
import sortBy from 'lodash/sortBy'
import { ORDER_EVENT_STATUS_EXPIRED } from '@klear/constants/order-event-status'
import type { KlearDate } from '@klear/date'
import type { Money } from '@klear/money'
import { fundingDocumentsNeedTrader, type FundingDocuments } from '@klear/invoice-offer/funding-documents'
import type { InvoiceOffer } from '@klear/invoice-offer/invoice-offer'
import { offerExtensionFeeTotal, offerFeeTotal, type OfferFee } from '@klear/invoice-offer/offer-fees'

export const RECEIVABLE_TABS = ['awaitingQuote', 'quotes', 'pending', 'live'] as const

export type ReceivableTab = (typeof RECEIVABLE_TABS)[number]

export type ReceivableRowTab = ReceivableTab | 'completed'

export type ReceivableCounts = Record<ReceivableTab, number> & { total: number }

type ReceivableInvoice = { id: number; status: string; effectiveDate: KlearDate; endDate: KlearDate }

export type Receivable<Invoice, PurchaseOrderEvent, Proof> = {
  tab: ReceivableRowTab
  offer?: InvoiceOffer
  invoice: Invoice
  documents: FundingDocuments<Invoice, PurchaseOrderEvent, Proof>
  fees?: Money
  extensionFeesCharged?: Money
  unpaidExtensionFees?: Money
  needsAction: boolean
}

function offerTab(offer: InvoiceOffer, invoice: ReceivableInvoice): ReceivableRowTab | null {
  if (offer.isCompleted) return 'completed'
  if (offer.canReturnAdvance) return 'live'
  if (offer.isAccepted) return 'pending'
  if (invoice.status === ORDER_EVENT_STATUS_EXPIRED) return null
  if (offer.isQuote) return 'quotes'
  return null
}

function needsAction(
  tab: ReceivableRowTab,
  offer: InvoiceOffer,
  documents: FundingDocuments<unknown, unknown, unknown>
): boolean {
  if (tab === 'quotes') return offer.isDraft
  if (tab === 'pending') return fundingDocumentsNeedTrader(documents)
  return false
}

function sortDate(row: Receivable<ReceivableInvoice, unknown, unknown>): string {
  const date = row.tab === 'live' ? (row.offer?.endDate ?? row.invoice.endDate) : row.invoice.effectiveDate
  return date.toJSON()
}

function completedDate(row: Receivable<ReceivableInvoice, unknown, unknown>): string {
  return (row.offer?.completedEffectiveDate ?? row.offer?.effectiveDate ?? row.invoice.effectiveDate).toJSON()
}

export function receivables<Invoice extends ReceivableInvoice, PurchaseOrderEvent, Proof>(input: {
  offers: readonly InvoiceOffer[]
  awaitingQuoteInvoiceIds: readonly number[]
  documents: ReadonlyMap<number, FundingDocuments<Invoice, PurchaseOrderEvent, Proof>>
  fees: readonly OfferFee[]
  unpaidExtensionFees: ReadonlyMap<number, Money>
}): Receivable<Invoice, PurchaseOrderEvent, Proof>[] {
  const rows: Receivable<Invoice, PurchaseOrderEvent, Proof>[] = []
  for (const invoiceId of input.awaitingQuoteInvoiceIds) {
    const documents = input.documents.get(invoiceId)
    const invoice = documents?.invoice.event
    if (documents == null || invoice == null) continue
    rows.push({ tab: 'awaitingQuote', invoice, documents, needsAction: false })
  }
  const feesByOfferId = groupBy(input.fees, (fee) => fee.parentId)
  for (const offer of input.offers) {
    const documents = input.documents.get(offer.parentId)
    const invoice = documents?.invoice.event
    if (documents == null || invoice == null) continue
    const tab = offerTab(offer, invoice)
    if (tab == null) continue
    const unpaidExtensionFees = input.unpaidExtensionFees.get(offer.id)
    rows.push({
      tab,
      offer,
      invoice,
      documents,
      fees: offerFeeTotal(offer, feesByOfferId[offer.id] ?? []),
      extensionFeesCharged: offerExtensionFeeTotal(offer, feesByOfferId[offer.id] ?? []),
      unpaidExtensionFees: unpaidExtensionFees?.isPositive() === true ? unpaidExtensionFees : undefined,
      needsAction: needsAction(tab, offer, documents)
    })
  }
  const [completed, open] = partition(rows, (row) => row.tab === 'completed')
  return [...sortBy(open, sortDate), ...orderBy(completed, [completedDate, (row) => row.offer?.id], ['desc', 'desc'])]
}

export function receivableCounts(rows: readonly { tab: ReceivableRowTab; needsAction: boolean }[]): ReceivableCounts {
  const counts: ReceivableCounts = { quotes: 0, awaitingQuote: 0, pending: 0, live: 0, total: 0 }
  for (const row of rows) {
    if (!row.needsAction || row.tab === 'completed') continue
    counts[row.tab] += 1
    counts.total += 1
  }
  return counts
}
